/**
 * The Bridge auction (rules §1.3–1.4).
 *
 * Thirty-five bids, plus pass, double and redouble. A bid must outrank the last
 * one; a double is of the opponents' last bid, undoubled; a redouble is of a
 * double against your own side. Four opening passes pass the board out; after
 * any bid, three consecutive passes end the auction.
 *
 * Declarer is the first player *of the side that won the contract* to have
 * named its denomination — not whoever bid it last. A transfer auction
 * (1NT – 2♦ – 2♥ – 4♥) is the case that breaks the naive version: the 1NT
 * opener named hearts first, so the opener declares.
 */
import { SUIT_LABEL } from '../../core/cards.js';
import { BRIDGE_STRAINS, } from './types.js';
const next = (s, n) => (s + 1) % n;
const sameSide = (a, b) => (a - b) % 2 === 0;
export function bidRank(level, strain) {
    return (level - 1) * 5 + BRIDGE_STRAINS.indexOf(strain);
}
export function bridgeStrainLabel(strain) {
    return strain === 'NT' ? 'NT' : SUIT_LABEL[strain];
}
export function bridgeContractLabel(c) {
    return `${c.level}${bridgeStrainLabel(c.strain)}${c.doubled === 2 ? ' XX' : c.doubled === 1 ? ' X' : ''}`;
}
/** The first player of `declarer`'s side to name `strain`. */
export function declarerOf(state, side, strain) {
    for (const { seat, call } of state.calls) {
        if (call.type === 'bid' && call.strain === strain && sameSide(seat, side))
            return seat;
    }
    return side;
}
export const bridgeAuction = {
    init({ table, dealer }) {
        return {
            seatCount: table.seatCount,
            dealer,
            calls: [],
            last: null,
            doubled: 0,
            doubledBy: null,
            passes: 0,
            // Rules §1.3: the dealer calls first.
            toAct: dealer,
            done: false,
        };
    },
    legalCalls(s, seat) {
        if (s.done || seat !== s.toAct)
            return [];
        const calls = [{ type: 'pass' }];
        const floor = s.last ? bidRank(s.last.level, s.last.strain) : -1;
        for (let level = 1; level <= 7; level++) {
            for (const strain of BRIDGE_STRAINS) {
                if (bidRank(level, strain) > floor)
                    calls.push({ type: 'bid', level: level, strain });
            }
        }
        if (s.last && !sameSide(s.last.seat, seat) && s.doubled === 0)
            calls.push({ type: 'double' });
        if (s.last && sameSide(s.last.seat, seat) && s.doubled === 1)
            calls.push({ type: 'redouble' });
        return calls;
    },
    apply(s, seat, call) {
        const calls = [...s.calls, { seat, call }];
        const toAct = next(seat, s.seatCount);
        switch (call.type) {
            case 'bid':
                return { ...s, calls, last: { seat, level: call.level, strain: call.strain }, doubled: 0, doubledBy: null, passes: 0, toAct };
            case 'double':
                return { ...s, calls, doubled: 1, doubledBy: seat, passes: 0, toAct };
            case 'redouble':
                return { ...s, calls, doubled: 2, passes: 0, toAct };
            default: {
                const passes = s.passes + 1;
                const done = s.last === null ? passes >= s.seatCount : passes >= s.seatCount - 1;
                return { ...s, calls, passes, done, toAct: done ? s.toAct : toAct };
            }
        }
    },
    status(s) {
        if (!s.done)
            return { kind: 'open', toAct: s.toAct };
        if (!s.last)
            return { kind: 'passedOut' };
        const c = {
            declarer: declarerOf(s, s.last.seat, s.last.strain),
            level: s.last.level,
            strain: s.last.strain,
            doubled: s.doubled,
        };
        return { kind: 'contract', contract: { ...c, label: bridgeContractLabel(c) } };
    },
    callKey(call) {
        return call.type === 'bid' ? `${call.level}${call.strain}` : call.type;
    },
    describe(call, _config) {
        switch (call.type) {
            case 'pass':
                return { label: 'Pass', explain: 'No bid. Three passes after a bid end the auction.' };
            case 'double':
                return { label: 'X', explain: 'Double: low down it asks partner to bid; higher up it says they will not make it.' };
            case 'redouble':
                return { label: 'XX', explain: 'Redouble: you say you will make it, for four times the stakes.' };
            case 'bid': {
                const tricks = call.level + 6;
                const strain = call.strain === 'NT' ? 'no trumps' : `${SUIT_LABEL[call.strain]} trump`;
                return {
                    label: `${call.level}${bridgeStrainLabel(call.strain)}`,
                    explain: `${strain}: your side takes ${tricks} of the 13 tricks.`,
                };
            }
        }
    },
};
//# sourceMappingURL=auction.js.map